import { CountUp } from 'countup.js';
import g from '#core/global';
import events from '#core/events';
import load from '#core/load';
import layout from '#core/layout';
import util from '#core/util';


const value_$ = '.statistic-number__value';
const countup_to_attr = 'data-countup-to';

let has_statistic_number = false;
let $scroll_values = null;
const has_run = [];
let counter_id = 0;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('statistic-number')) return false;

		load.all(() => {
			has_statistic_number = assign_elements();
			if (!has_statistic_number) return false;

			// Reduced motion: every number is already at its final value (the
			// count-up ran with duration 0), including ones below the fold, so
			// there is nothing for a scroll listener to trigger.
			if (prefers_reduced_motion()) return true;

			assign_scroll_events();
		});
	},


	/**
	 * Land every number on its final value at once, wherever it sits on the
	 * page. Printing needs this: a number below the fold has never scrolled
	 * into view, so it would otherwise print as 0.
	 */
	finish() {
		if (!assign_elements()) return false;

		document.querySelectorAll(`${value_$}[${countup_to_attr}]`).forEach($el => {
			if (has_run.includes($el.id)) return false;

			has_run.push($el.id);
			run_countup($el, true);
		});
	},

};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_statistic_number) return true;

	const $values = document.querySelectorAll(`${value_$}[${countup_to_attr}]`);
	if (!$values.length) return false;

	const scroll_managed = [];

	$values.forEach($el => {
		if (!$el.id) {
			$el.id = 'statistic-number-' + (++counter_id);
		}

		const $reveal = $el.closest(
			`[${g.reveal_attr}], [${g.revealing_attr}], [${g.revealed_attr}]`
		);

		if ($reveal) {
			bind_to_reveal($el, $reveal);
		} else {
			scroll_managed.push($el);
		}
	});

	$scroll_values = scroll_managed;

	// A number that never scrolls into view would otherwise sit at 0 forever
	// for a reduced-motion visitor, so run the whole set now rather than
	// waiting on intersection.
	if (prefers_reduced_motion()) {
		$values.forEach($el => {
			if (has_run.includes($el.id)) return;
			has_run.push($el.id);
			run_countup($el);
		});
		return true;
	}

	check_countups();
	return true;
}


/**
 * Does this visitor want reduced motion?
 * @return {boolean}
 */
function prefers_reduced_motion() {
	return typeof window.matchMedia === 'function'
		&& window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}


/**
 * Bind countup to a reveal ancestor's lifecycle
 * @param {Element} $el
 * @param {Element} $reveal
 */
function bind_to_reveal($el, $reveal) {
	if (
		$reveal.hasAttribute(g.revealed_attr)
		|| $reveal.hasAttribute(g.revealing_attr)
	) {
		run_countup($el);
	} else {
		$reveal.addEventListener(events.reveal.start, () => {
			run_countup($el);
		}, { once: true });
	}
}


/**
 * Assign scroll events
 */
function assign_scroll_events() {
	window.addEventListener(g.scroll_e, util.throttle(() => {
		check_countups();
	}, 250));
}


/**
 * Run CountUp animation on element
 * @param {Element} $element
 * @param {boolean} instant
 */
function run_countup($element, instant = false) {
	const id = $element.id;
	const countup_to = parseFloat($element.getAttribute(countup_to_attr));
	if (!id || isNaN(countup_to)) return false;

	const has_decimals = countup_to % 1 !== 0;

	// Duration 0 lands on the final value in one step. This is also what makes
	// the component screenshot-stable: a 2.5-second count caught by a
	// visual-regression capture yields whichever digits happened to be on
	// screen at that instant, so two captures of an unchanged page disagree on
	// exactly the digits still moving. Measured 2026-08-25 — impact-landing and
	// home differed by ~0.04%, entirely inside these numbers.
	let countup = new CountUp(id, countup_to, {
		duration: (instant || prefers_reduced_motion()) ? 0 : 2.5,
		decimalPlaces: has_decimals ? 1 : 0,
		useGrouping: true,
	});

	if (!countup.error) {
		countup.start();
	}
}


/**
 * Check if scroll-managed values are visible and trigger countup
 */
function check_countups() {
	if (!$scroll_values) return false;

	$scroll_values.forEach($el => {
		if (has_run.includes($el.id)) return false;

		const is_visible = layout.isIntersecting({
			$element: $el,
			lookaround: 1,
		});
		if (!is_visible) return false;

		has_run.push($el.id);
		run_countup($el);
	});
}
